import * as XLSX from 'xlsx'

export const MAX_WORKBOOK_FILE_BYTES = 25 * 1024 * 1024
export const MAX_WORKBOOK_UNCOMPRESSED_BYTES = 200 * 1024 * 1024
export const MAX_WORKBOOK_SHEETS = 20
export const MAX_WORKBOOK_ROWS = 20_000
export const MAX_WORKBOOK_COLUMNS = 256

export interface WorkbookFileLike {
  name: string
  size: number
  arrayBuffer(): Promise<ArrayBuffer>
}

export function assertWorkbookFileSize(size: number): void {
  if (size > MAX_WORKBOOK_FILE_BYTES) {
    throw new Error('File Excel vượt quá giới hạn 25 MB.')
  }
}

function hasZipSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b &&
    ((bytes[2] === 0x03 && bytes[3] === 0x04) ||
      (bytes[2] === 0x05 && bytes[3] === 0x06) ||
      (bytes[2] === 0x07 && bytes[3] === 0x08))
}

function hasOleSignature(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]
    .every((value, index) => bytes[index] === value)
}

function looksLikeText(bytes: Uint8Array): boolean {
  if (bytes.some((b) => b === 0)) return false
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    return true
  } catch {
    return bytes[0] === 0xff && bytes[1] === 0xfe || bytes[0] === 0xfe && bytes[1] === 0xff
  }
}

function validateWorkbookPayload(bytes: Uint8Array): void {
  if (!hasZipSignature(bytes) && !hasOleSignature(bytes) && !looksLikeText(bytes)) {
    throw new Error('Nội dung file không đúng định dạng Excel hoặc CSV được hỗ trợ.')
  }
}

export function validateExcelPayload(bytes: Uint8Array): void {
  assertWorkbookFileSize(bytes.byteLength)
  if (!hasZipSignature(bytes) && !hasOleSignature(bytes)) {
    throw new Error('Nội dung file không phải workbook Excel (XLSX, XLSM hoặc XLS).')
  }
  if (hasZipSignature(bytes)) assertZipExpansionSafe(bytes)
}

function assertZipExpansionSafe(bytes: Uint8Array): void {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const minEocd = Math.max(0, bytes.length - 22 - 0xffff)
  let eocd = -1
  for (let offset = bytes.length - 22; offset >= minEocd; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) {
      eocd = offset
      break
    }
  }
  if (eocd < 0 || eocd + 22 > bytes.length) {
    throw new Error('Cấu trúc ZIP của workbook không hợp lệ.')
  }
  const disk = view.getUint16(eocd + 4, true)
  const centralDisk = view.getUint16(eocd + 6, true)
  const diskEntries = view.getUint16(eocd + 8, true)
  const totalEntries = view.getUint16(eocd + 10, true)
  const centralSize = view.getUint32(eocd + 12, true)
  const centralOffset = view.getUint32(eocd + 16, true)
  const commentLength = view.getUint16(eocd + 20, true)
  if (eocd + 22 + commentLength !== bytes.length || disk !== 0 || centralDisk !== 0 || diskEntries !== totalEntries) {
    throw new Error('Cấu trúc ZIP nhiều đĩa hoặc không hợp lệ, không thể kiểm tra an toàn.')
  }
  if (totalEntries === 0xffff || centralSize === 0xffffffff || centralOffset === 0xffffffff || centralOffset + centralSize > eocd) {
    throw new Error('Workbook ZIP64 hoặc quá lớn để kiểm tra an toàn.')
  }
  let offset = centralOffset
  let uncompressedTotal = 0
  for (let entry = 0; entry < totalEntries; entry += 1) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== 0x02014b50) {
      throw new Error('Danh mục ZIP của workbook không hợp lệ.')
    }
    let uncompressed = view.getUint32(offset + 24, true)
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const extraStart = offset + 46 + nameLength
    const entryEnd = extraStart + extraLength + commentLength
    if (entryEnd > bytes.length || entryEnd > centralOffset + centralSize) {
      throw new Error('Mục trong danh mục ZIP của workbook không hợp lệ.')
    }
    if (uncompressed === 0xffffffff) {
      let extra = extraStart
      let found = false
      while (extra + 4 <= extraStart + extraLength) {
        const kind = view.getUint16(extra, true)
        const length = view.getUint16(extra + 2, true)
        if (extra + 4 + length > extraStart + extraLength) break
        if (kind === 0x0001 && length >= 8) {
          const low = view.getUint32(extra + 4, true)
          const high = view.getUint32(extra + 8, true)
          uncompressed = high === 0 ? low : MAX_WORKBOOK_UNCOMPRESSED_BYTES + 1
          found = true
          break
        }
        extra += 4 + length
      }
      if (!found) throw new Error('Workbook ZIP64 thiếu kích thước giải nén để kiểm tra an toàn.')
    }
    uncompressedTotal += uncompressed
    if (uncompressedTotal > MAX_WORKBOOK_UNCOMPRESSED_BYTES) {
      throw new Error('Workbook sau giải nén vượt quá giới hạn an toàn 200 MB.')
    }
    offset = entryEnd
  }
}

function hasData(cell: unknown): boolean {
  if (!cell || typeof cell !== 'object') return false
  const value = (cell as XLSX.CellObject).v
  const formula = (cell as XLSX.CellObject).f
  return value !== undefined && value !== null && value !== '' || Boolean(formula)
}

function assertDataLimits(sheet: XLSX.WorkSheet, name: string): void {
  const populatedRows = new Set<number>()
  let maxPopulatedRow = -1
  let maxPopulatedColumn = -1
  for (const [address, cell] of Object.entries(sheet)) {
    if (!/^[A-Z]{1,3}[1-9]\d*$/.test(address) || !hasData(cell)) continue
    const { r: row, c: column } = XLSX.utils.decode_cell(address)
    if (row > maxPopulatedRow) maxPopulatedRow = row
    if (column > maxPopulatedColumn) maxPopulatedColumn = column
    populatedRows.add(row)
  }
  if (populatedRows.size > MAX_WORKBOOK_ROWS || maxPopulatedRow >= MAX_WORKBOOK_ROWS) {
    throw new Error(`Sheet "${name}" vượt quá giới hạn ${MAX_WORKBOOK_ROWS.toLocaleString('vi-VN')} dòng có dữ liệu.`)
  }
  if (maxPopulatedColumn + 1 > MAX_WORKBOOK_COLUMNS) {
    throw new Error(`Sheet "${name}" vượt quá giới hạn ${MAX_WORKBOOK_COLUMNS} cột có dữ liệu.`)
  }
}

export function validateWorkbookBytes(fileName: string, bytes: Uint8Array): void {
  const extension = fileName.toLowerCase().match(/\.[^.]+$/)?.[0]
  if (!['.xlsx', '.xls', '.xlsm', '.csv'].includes(extension ?? '')) {
    throw new Error('Định dạng file không được hỗ trợ. Vui lòng chọn .xlsx, .xls, .xlsm hoặc .csv.')
  }
  assertWorkbookFileSize(bytes.byteLength)
  validateWorkbookPayload(bytes)
  const matches = extension === '.xlsx' || extension === '.xlsm'
    ? hasZipSignature(bytes)
    : extension === '.xls'
      ? hasOleSignature(bytes)
      : looksLikeText(bytes)
  if (!matches) {
    throw new Error('Nội dung file không khớp với định dạng phần mở rộng đã chọn.')
  }
  if (extension === '.xlsx' || extension === '.xlsm') assertZipExpansionSafe(bytes)
}

export function readSafeWorkbook(
  data: ArrayBuffer | Uint8Array,
  options: Partial<XLSX.ParsingOptions> = {},
): XLSX.WorkBook {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data)
  assertWorkbookFileSize(bytes.byteLength)
  validateWorkbookPayload(bytes)
  if (hasZipSignature(bytes)) assertZipExpansionSafe(bytes)
  let workbook: XLSX.WorkBook
  try {
    workbook = XLSX.read(bytes, {
      type: 'array',
      cellHTML: false,
      ...options,
      // Keep formulas available so a formula-only row still counts as populated.
      cellFormula: true,
      sheetRows: MAX_WORKBOOK_ROWS + 1,
    })
  } catch {
    throw new Error('Không thể đọc file Excel. Vui lòng kiểm tra lại định dạng và nội dung file.')
  }
  if (workbook.SheetNames.length > MAX_WORKBOOK_SHEETS) {
    throw new Error(`File Excel vượt quá giới hạn ${MAX_WORKBOOK_SHEETS} sheet.`)
  }
  for (const name of workbook.SheetNames) {
    const sheet = workbook.Sheets[name]
    if (!sheet) continue
    assertDataLimits(sheet, name)
  }
  return workbook
}

export async function readSafeWorkbookFile(
  file: WorkbookFileLike,
  options: Partial<XLSX.ParsingOptions> = {},
): Promise<XLSX.WorkBook> {
  assertWorkbookFileSize(file.size)
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  validateWorkbookBytes(file.name, bytes)
  return readSafeWorkbook(bytes, options)
}
